import React, { useEffect, useMemo, useState } from "react";

import { getTBMapUrl } from "../../services/tbOutbreakApi";

export default function TBMapPanel({
  countryIso = "SE",
  title,
  scopeLabel,
  description,
}) {
  const [mapHtml, setMapHtml] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    setMapHtml("");
    setError("");

    fetch(getTBMapUrl(countryIso), {
      cache: "no-store",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Could not load map (${response.status} ${response.statusText})`
          );
        }

        return response.text();
      })
      .then((html) => {
        if (!active) {
          return;
        }

        setMapHtml(html);
      })
      .catch((err) => {
        console.error("Failed to load TB map:", err);

        if (active) {
          setError(err.message);
        }
      });

    return () => {
      active = false;
    };
  }, [countryIso]);

  const renderedMapHtml = useMemo(() => {
    if (!mapHtml) {
      return "";
    }

    const dashboardOverrides = `
      <style>
        /*
         * The Folium output contains its own fixed legend.
         * The dashboard renders that legend outside the map,
         * so hide only that generated overlay.
         */
        body > div[style*="z-index:9999"] {
          display: none !important;
        }

        /*
         * Keep the Leaflet TimeDimension control compact inside
         * the dashboard map without changing its functionality.
         */
        .leaflet-bar-timecontrol {
          transform: scale(0.78);
          transform-origin: bottom left;
        }
      </style>
    `;

    return mapHtml.replace(
      "</head>",
      `${dashboardOverrides}</head>`
    );
  }, [mapHtml]);

  return (
    <div className="tb-chart-panel tb-map-panel">
      <div className="tb-chart-panel-header">
        <div>
          {scopeLabel && (
            <div className="tb-chart-scope">
              {scopeLabel}
            </div>
          )}

          <h3>{title}</h3>

          {description && (
            <p>{description}</p>
          )}
        </div>
      </div>

      <div className="tb-map-content">
        {error && (
          <div className="tb-chart-error">
            <strong>Could not load the map.</strong>
            <span>{error}</span>
          </div>
        )}

        {!renderedMapHtml && !error && (
          <div className="tb-chart-loading">
            Loading map...
          </div>
        )}

        {renderedMapHtml && (
          <iframe
            className="tb-map-frame"
            srcDoc={renderedMapHtml}
            title={title}
            sandbox="allow-scripts"
          />
        )}

        <div className="tb-map-legend">
          <div className="tb-map-legend-title">
            Cases requiring action
          </div>

          <div className="tb-map-legend-section">
            <strong>Priority tier</strong>

            <div className="tb-map-legend-items">
              <span>
                <i className="tb-map-dot tb-map-dot-urgent" />
                Urgent clinical follow-up
              </span>

              <span>
                <i className="tb-map-dot tb-map-dot-high" />
                High-priority contact investigation
              </span>

              <span>
                <i className="tb-map-dot tb-map-dot-monitoring" />
                Enhanced monitoring
              </span>
            </div>

            <small>
              Larger circles indicate higher priority.
            </small>
          </div>

          <div className="tb-map-legend-section">
            <strong>Cluster epicentres</strong>

            <div className="tb-map-legend-items">
              <span>
                <i className="tb-map-dot tb-map-dot-urgent" />
                Cluster signal
              </span>

              <span>
                <i className="tb-map-dot tb-map-dot-high" />
                Elevated
              </span>

              <span>
                <i className="tb-map-dot tb-map-dot-normal" />
                Within expectation
              </span>
            </div>

            <small>
              Ring size is proportional to excess over expected.
            </small>
          </div>
        </div>
      </div>
    </div>
  );
}
